import { createApi } from '@reduxjs/toolkit/query/react';
import { OKVED_BASE_URL } from 'shared/api/api';
import { createBaseQuery } from 'shared/api/rtk-query';
import { CreatedOkvedGroupDto, CreatedOkvedPartitionDto, OkvedGroupDto, OkvedPartitionDto } from '@sr/dto';

const OKVED_PATH = OKVED_BASE_URL;

export const okvedApi = createApi({
  reducerPath: 'okved',
  baseQuery: createBaseQuery(),
  tagTypes: ['OkvedPartition', 'OkvedGroup'],
  endpoints: (builder) => ({

    getOkvedPartitionList: builder.query<OkvedPartitionDto[], void>({
      query: () => ({
        url: `${OKVED_PATH}/partition`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'OkvedPartition' as const, id })),
            { type: 'OkvedPartition', id: 'LIST' }
          ]
          : [{ type: 'OkvedPartition', id: 'LIST' }]
    }),
    getOkvedDropdown: builder.query<OkvedGroupDto[], void>({
      query: () => ({
        url: `${OKVED_BASE_URL}/dropdown`,
        method: 'GET'
      })
    }),
    
    getOkvedGroupList: builder.query<OkvedGroupDto[], number>({
      query: (id: number) => ({
        url: `${OKVED_PATH}/group/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'OkvedGroup' as const, id })),
            { type: 'OkvedGroup', id: `PARTITION-${id}` }
          ]
          : [{ type: 'OkvedGroup', id: `PARTITION-${id}` }]
    }),
    
    createOkvedPartition: builder.mutation<OkvedPartitionDto, CreatedOkvedPartitionDto>({
      query: (dto) => ({
        url: `${OKVED_PATH}/partition/create`,
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'OkvedPartition', id: 'LIST' }]
    }),

    createOkvedGroup: builder.mutation<OkvedGroupDto, CreatedOkvedGroupDto>({
      query: (dto) => ({
        url: `${OKVED_PATH}/group/create`,
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { okvedId }) => [
        { type: 'OkvedGroup', id: `PARTITION-${okvedId}` }
      ]
    }),
    deleteOkvedPartition: builder.mutation<void, number>({
      query: (id: number) => ({
        url: `${OKVED_PATH}/partition/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id ) => [
        { type: 'OkvedPartition', id: 'LIST' },
        { type: 'OkvedGroup', id: `PARTITION-${id}` }]
    }),
    deleteOkvedGroup: builder.mutation<void, { id: number; partitionId: number }>({
      query: ({ id }) => ({
        url: `${OKVED_PATH}/group/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, { partitionId }) => [
        { type: 'OkvedGroup', id: `PARTITION-${partitionId}` }
      ]
    })
  })
});

export const {
  useGetOkvedPartitionListQuery,
  useGetOkvedGroupListQuery,
  useCreateOkvedPartitionMutation,
  useCreateOkvedGroupMutation,
  useDeleteOkvedPartitionMutation,
  useDeleteOkvedGroupMutation,
  useGetOkvedDropdownQuery
} = okvedApi;
